/**
 * SupervisionDashboardPage
 *
 * Tabs: Dashboard, Co-sign Notes (supervisor), Pending/Missing Documents,
 * My Unsigned Notes (supervisor own drafts; provider Unsigned Notes).
 */

import { Box, Container, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconFileText, IconLayoutDashboard } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useCosignQueueFromMedplum } from './hooks/useCosignQueueFromMedplum';
import { useMissingDocumentsFromMedplum } from './hooks/useMissingDocumentsFromMedplum';
import { useSupervisionScope } from './hooks/useSupervisionScope';
import { CosignNotesTab, DashboardTab, MissingDocumentsTab } from './tabs';

const SUPERVISION_BASE = '/dashboard/supervision';

function pathToTab(pathname: string): string {
  if (pathname === SUPERVISION_BASE || pathname === `${SUPERVISION_BASE}/`) return 'dashboard';
  if (pathname.startsWith(`${SUPERVISION_BASE}/cosign-notes`)) return 'cosign-notes';
  if (pathname.startsWith(`${SUPERVISION_BASE}/unsigned-notes`)) return 'unsigned-notes';
  if (pathname.startsWith(`${SUPERVISION_BASE}/missing-documents`)) return 'missing-documents';
  return 'dashboard';
}

function countBadge(count: number): JSX.Element | null {
  if (!count) return null;
  return (
    <Box
      component="span"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 20,
        height: 20,
        paddingLeft: String(count).length > 2 ? 6 : 0,
        paddingRight: String(count).length > 2 ? 6 : 0,
        borderRadius: 9999,
        backgroundColor: 'var(--mantine-color-red-6)',
        color: 'white',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {count}
    </Box>
  );
}

export function SupervisionDashboardPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useSupervisionScope();
  const isSupervisor = role === 'supervisor';
  const activeTab = useMemo(() => pathToTab(location.pathname), [location.pathname]);
  const { items: cosignQueueItems } = useCosignQueueFromMedplum();
  const { items: ownUnsignedItems } = useCosignQueueFromMedplum({ queue: 'own-unsigned' });
  const { items: missingDocItems } = useMissingDocumentsFromMedplum();
  const cosignCountLabel = cosignQueueItems.length;
  const unsignedCountLabel = isSupervisor ? ownUnsignedItems.length : cosignQueueItems.length;
  const missingDocumentsCountLabel = useMemo(() => {
    return missingDocItems.filter((i) => {
      if (i.status === 'overdue') return true;
      if (i.status === 'due-soon') {
        if (i.category === 'treatment-plans') return true;
        if (i.category === 'forms' && i.formType === 'intake') return true;
      }
      return false;
    }).length;
  }, [missingDocItems]);

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      navigate(value === 'dashboard' ? SUPERVISION_BASE : `${SUPERVISION_BASE}/${value}`, { replace: true });
    },
    [navigate]
  );

  if (location.pathname === `${SUPERVISION_BASE}/quality` || location.pathname.startsWith(`${SUPERVISION_BASE}/quality/`)) {
    return <Navigate to={SUPERVISION_BASE} replace />;
  }
  if (
    !isSupervisor &&
    (location.pathname === `${SUPERVISION_BASE}/unsigned-notes` || location.pathname.startsWith(`${SUPERVISION_BASE}/unsigned-notes/`))
  ) {
    return <Navigate to={`${SUPERVISION_BASE}/cosign-notes`} replace />;
  }
  const validTabSlugs = [
    'cosign-notes',
    'missing-documents',
    ...(isSupervisor ? ['unsigned-notes'] : []),
  ];
  const pathSegment = location.pathname.replace(/^\/dashboard\/supervision\/?/, '').split('/')[0] || '';
  if (pathSegment && !validTabSlugs.includes(pathSegment) && pathSegment !== 'review') {
    return <Navigate to={SUPERVISION_BASE} replace />;
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>Supervision</Title>
          <Text c="gray.6" size="sm" mt="xs">
            Review your unsigned documentation and missing items
          </Text>
        </div>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          styles={{ tab: { transition: 'all 0.2s ease' } }}
          style={{ '--tabs-list-border-size': '2px' } as React.CSSProperties}
        >
          <Tabs.List>
            <Tabs.Tab value="dashboard" leftSection={<IconLayoutDashboard size={16} />}>
              Dashboard
            </Tabs.Tab>
            <Tabs.Tab
              value="cosign-notes"
              leftSection={<IconFileText size={16} />}
              rightSection={countBadge(isSupervisor ? cosignCountLabel : unsignedCountLabel)}
            >
              {isSupervisor ? 'Co-sign Notes' : 'Unsigned Notes'}
            </Tabs.Tab>
            <Tabs.Tab
              value="missing-documents"
              leftSection={<IconAlertCircle size={16} />}
              rightSection={countBadge(missingDocumentsCountLabel)}
            >
              {isSupervisor ? 'Pending Documents' : 'Missing Documents'}
            </Tabs.Tab>
            {isSupervisor ? (
              <Tabs.Tab
                value="unsigned-notes"
                leftSection={<IconFileText size={16} />}
                rightSection={countBadge(unsignedCountLabel)}
              >
                My Unsigned Notes
              </Tabs.Tab>
            ) : null}
          </Tabs.List>

          <Tabs.Panel value="dashboard" pt="xl">
            <DashboardTab />
          </Tabs.Panel>
          <Tabs.Panel value="cosign-notes" pt="xl">
            <CosignNotesTab />
          </Tabs.Panel>
          <Tabs.Panel value="missing-documents" pt="xl">
            <MissingDocumentsTab />
          </Tabs.Panel>
          {isSupervisor ? (
            <Tabs.Panel value="unsigned-notes" pt="xl">
              <CosignNotesTab queue="own-unsigned" />
            </Tabs.Panel>
          ) : null}
        </Tabs>
      </Stack>
    </Container>
  );
}
